/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Global Error Handling & Recovery System
 */

import React from 'react';

export interface ErrorLog {
  id: string;
  timestamp: number;
  level: 'debug' | 'info' | 'warn' | 'error' | 'critical';
  message: string;
  context?: Record<string, any>;
  stack?: string;
  userAction?: string;
  gameState?: {
    tick: number;
    budget: number;
    compliance: number;
    threat: number;
  };
}

export interface ErrorRecoveryStrategy {
  type: 'retry' | 'fallback' | 'reset' | 'notify';
  maxRetries?: number;
  fallbackValue?: any;
  resetTo?: 'checkpoint' | 'mainMenu';
  notifyUser?: boolean;
}

/**
 * Global error logger service
 */
export class ErrorLogger {
  private static instance: ErrorLogger;
  private logs: ErrorLog[] = [];
  private maxLogs = 500;
  private logLevel: 'debug' | 'info' | 'warn' | 'error' | 'critical' = 'info';

  private constructor() {
    this.loadLogsFromStorage();
    this.setupGlobalErrorHandlers();
  }

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * Log a message with context
   */
  log(
    level: 'debug' | 'info' | 'warn' | 'error' | 'critical',
    message: string,
    context?: Record<string, any>,
    stack?: string
  ): ErrorLog {
    const log: ErrorLog = {
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      level,
      message,
      context,
      stack
    };

    this.logs.push(log);

    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Save to localStorage periodically
    if (this.logs.length % 50 === 0) {
      this.saveLogsToStorage();
    }

    // Console output
    this.outputToConsole(log);

    // Critical errors should be reported
    if (level === 'critical') {
      this.reportCriticalError(log);
    }

    return log;
  }

  debug(message: string, context?: Record<string, any>): ErrorLog {
    return this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, any>): ErrorLog {
    return this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, any>): ErrorLog {
    return this.log('warn', message, context);
  }

  error(message: string, context?: Record<string, any>, stack?: string): ErrorLog {
    return this.log('error', message, context, stack);
  }

  critical(message: string, context?: Record<string, any>, stack?: string): ErrorLog {
    return this.log('critical', message, context, stack);
  }

  /**
   * Get all logs or filter by level
   */
  getLogs(level?: string): ErrorLog[] {
    if (!level) return this.logs;
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
    localStorage.removeItem('game_error_logs');
  }

  /**
   * Export logs for debugging
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Set game state context for error logs
   */
  setGameStateContext(gameState: ErrorLog['gameState']): void {
    if (this.logs.length > 0) {
      this.logs[this.logs.length - 1].gameState = gameState;
    }
  }

  /**
   * Set user action context
   */
  setUserAction(action: string): void {
    if (this.logs.length > 0) {
      this.logs[this.logs.length - 1].userAction = action;
    }
  }

  private outputToConsole(log: ErrorLog): void {
    const style = this.getConsoleStyle(log.level);
    const prefix = `[${log.level.toUpperCase()}]`;

    console.log(
      `%c${prefix} ${log.message}`,
      style,
      log.context || '',
      log.stack || ''
    );
  }

  private getConsoleStyle(level: string): string {
    const styles = {
      debug: 'color: #888; font-size: 12px;',
      info: 'color: #0066cc; font-weight: bold;',
      warn: 'color: #ff9900; font-weight: bold;',
      error: 'color: #cc0000; font-weight: bold;',
      critical: 'color: #cc0000; font-weight: bold; background: #ffcccc; padding: 2px 4px;'
    };
    return styles[level] || styles.info;
  }

  private setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      this.error(
        `Uncaught Error: ${event.message}`,
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        },
        event.error?.stack
      );
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error(
        `Unhandled Promise Rejection: ${event.reason}`,
        {},
        event.reason?.stack
      );
    });
  }

  private saveLogsToStorage(): void {
    try {
      const recentLogs = this.logs.slice(-100); // Keep only recent logs
      localStorage.setItem('game_error_logs', JSON.stringify(recentLogs));
    } catch (e) {
      console.warn('Failed to save error logs to storage');
    }
  }

  private loadLogsFromStorage(): void {
    try {
      const stored = localStorage.getItem('game_error_logs');
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to load error logs from storage');
    }
  }

  private reportCriticalError(log: ErrorLog): void {
    // In production, this would send to a monitoring service like Sentry
    console.error('CRITICAL ERROR DETECTED:', log);
    
    // Could integrate with backend:
    // fetch('/api/errors', { method: 'POST', body: JSON.stringify(log) });
  }
}

/**
 * Error recovery utilities
 */
export class ErrorRecovery {
  private static checkpoints: Map<string, any> = new Map();
  private static logger = ErrorLogger.getInstance();

  /**
   * Create a checkpoint for recovery
   */
  static createCheckpoint(name: string, state: any): void {
    this.checkpoints.set(name, JSON.parse(JSON.stringify(state)));
    this.logger.debug(`Checkpoint created: ${name}`);
  }

  /**
   * Recover from a checkpoint
   */
  static recoverFromCheckpoint(name: string): any {
    const checkpoint = this.checkpoints.get(name);
    if (!checkpoint) {
      this.logger.warn(`Checkpoint not found: ${name}`);
      return null;
    }
    this.logger.info(`Recovered from checkpoint: ${name}`);
    return JSON.parse(JSON.stringify(checkpoint));
  }

  /**
   * List all available checkpoints
   */
  static listCheckpoints(): string[] {
    return Array.from(this.checkpoints.keys());
  }

  /**
   * Clear a checkpoint
   */
  static clearCheckpoint(name: string): void {
    this.checkpoints.delete(name);
  }

  /**
   * Clear all checkpoints
   */
  static clearAllCheckpoints(): void {
    this.checkpoints.clear();
  }

  /**
   * Execute function with retry logic
   */
  static async executeWithRetry<T>(
    fn: () => Promise<T> | T,
    maxRetries: number = 3,
    delayMs: number = 1000
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let i = 0; i < maxRetries; i++) {
      try {
        return await Promise.resolve(fn());
      } catch (error) {
        lastError = error as Error;
        this.logger.warn(`Attempt ${i + 1}/${maxRetries} failed: ${lastError.message}`);

        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs * (i + 1)));
        }
      }
    }

    throw lastError || new Error('Max retries exceeded');
  }

  /**
   * Execute function with fallback value
   */
  static executeWithFallback<T>(
    fn: () => T,
    fallback: T,
    errorMessage?: string
  ): T {
    try {
      return fn();
    } catch (error) {
      this.logger.warn(
        errorMessage || `Operation failed, using fallback value`,
        { error: (error as Error).message }
      );
      return fallback;
    }
  }
}

/**
 * React Error Boundary Component
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  private logger = ErrorLogger.getInstance();

  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.logger.critical(
      `React Error Boundary caught: ${error.message}`,
      { componentStack: errorInfo.componentStack },
      error.stack
    );
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return React.createElement(
        'div',
        {
          style: {
            padding: '20px',
            backgroundColor: '#ffcccc',
            border: '2px solid #cc0000',
            borderRadius: '4px',
            margin: '20px'
          }
        },
        React.createElement('h2', null, '⚠️ Something went wrong'),
        React.createElement('p', null, this.state.error?.message),
        React.createElement(
          'button',
          {
            onClick: () => window.location.reload(),
            style: {
              padding: '10px 20px',
              backgroundColor: '#cc0000',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }
          },
          'Reload Game'
        ),
        React.createElement(
          'details',
          { style: { marginTop: '10px' } },
          React.createElement('summary', null, 'Error Details'),
          React.createElement(
            'pre',
            { style: { fontSize: '12px', overflow: 'auto' } },
            this.state.error?.stack
          )
        )
      );
    }

    return this.props.children;
  }
}

// Export singleton instance
export const errorLogger = ErrorLogger.getInstance();

/** Log a critical failure (save/load corruption, tick abort, etc.) */
export const logCritical = (
  message: string,
  context?: Record<string, unknown>,
  stack?: string,
): ErrorLog => errorLogger.critical(message, context, stack);
